import React, { useState } from "react";
import styled, { css } from "styled-components";

import Fab from "@mui/material/Fab";
import { Status } from "@enums/statistic";
import { useAppDispatch } from "@hooks/store";
import { setFilters } from "@store/slices/stats";
import { device } from "@scss/constants/mediaQuery";

import type { FC } from "react";
import type { Filters } from "@interfaces/stats";

type StatsFilterProps = {
  filters: Partial<Filters>;
  onTimePercent: string;
};

const StatsFilters: FC<StatsFilterProps> = ({ filters, onTimePercent }) => {
  const dispatch = useAppDispatch();
  const [currentFilters, setCurrentFilters] = useState(filters);
  const isValidPercents = !isNaN(Number(onTimePercent));

  const sx = {
    height: "40%",
    borderRadius: "5px",
    backgroundColor: "white",
    color: "black",
    zIndex: 2,
  };

  const onFabClick = ({
    currentTarget,
  }: React.MouseEvent<HTMLButtonElement>) => {
    const name = currentTarget.name as Status;
    const updatedFilters = { ...currentFilters, [name]: !currentFilters[name] };

    setCurrentFilters(updatedFilters);
    dispatch(setFilters(updatedFilters));
  };

  return (
    <Wrapper>
      <Container>
        <StyledFab
          sx={sx}
          variant="extended"
          onClick={onFabClick}
          name={Status.ScheduledPU}
          $selected={currentFilters[Status.ScheduledPU]}
        >
          {Status.ScheduledPU}
        </StyledFab>

        <StyledFab
          sx={sx}
          variant="extended"
          onClick={onFabClick}
          name={Status.AtReceiver}
          $selected={currentFilters[Status.AtReceiver]}
        >
          {Status.AtReceiver}
        </StyledFab>
      </Container>

      <Container>
        <StyledFab
          sx={sx}
          variant={"extended"}
          onClick={onFabClick}
          name={Status.InTransit}
          $selected={currentFilters[Status.InTransit]}
        >
          {Status.InTransit}
        </StyledFab>

        <StyledFab
          sx={sx}
          variant="extended"
          onClick={onFabClick}
          name={Status.Delivered}
          $selected={currentFilters[Status.Delivered]}
        >
          {Status.Delivered}
        </StyledFab>
      </Container>

      <Container>
        <StyledFab
          variant="extended"
          sx={sx}
          $height={100}
          $width={100}
          $cursor="default"
        >
          <FabContent>
            <OnTimeTitle>On time</OnTimeTitle>
            {isValidPercents && <OnTimeContent>{onTimePercent}%</OnTimeContent>}
          </FabContent>
        </StyledFab>
      </Container>
    </Wrapper>
  );
};

const FabContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const OnTimeContent = styled.div`
  color: #1f8322;
  font-size: 14pt;
`;

const OnTimeTitle = styled.div`
  font-size: 16pt;
`;

const StyledFab = styled(Fab)<{
  $selected?: boolean;
  $cursor?: string;
  $height?: number;
  $width?: number;
}>`
  width: ${({ $width }) => $width && $width}% !important;
  height: ${({ $height }) => $height && $height}% !important;

  border-radius: 5px !important;

  background-color: ${({ $selected }) => $selected && "#1976d2"} !important;
  color: ${({ $selected }) => $selected && "#fff"} !important;

  cursor: ${({ $cursor }) => $cursor && $cursor} !important;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  min-height: 96px !important;
  width: 100% !important;
  margin: 0 auto 20px;
  min-width: 150px;

  ${device.mobileL(css`
    width: calc(100% - 125px) !important;
  `)}

  ${device.tablet(css`
    width: 30% !important;
  `)}
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  height: 60%;
  justify-content: space-between;
  flex-direction: column;

  margin: 0 auto;

  ${device.tablet(css`
    flex-direction: row;
    flex-wrap: wrap;
    width: 90%;
  `)}
`;

export default StatsFilters;
